<template>
  <div class="flex items-center gap-1.5">
    <select
      class="flex-1 text-sm bg-slate-700/80 text-white border border-slate-500 rounded px-2 py-1 focus:outline-none focus:border-cyan-500 appearance-none"
      :value="modelValue || ''"
      @change="onChange($event.target.value)"
    >
      <option value="">— No {{ resourceType }} —</option>
      <option v-for="item in items" :key="item.uuid" :value="item.uuid">
        {{ labelFor(item) }}
      </option>
    </select>
    <button
      v-if="modelValue"
      class="text-slate-400 hover:text-red-400 p-1 rounded hover:bg-slate-600 transition-colors flex-shrink-0"
      :title="`Clear ${resourceType}`"
      @click="onChange('')"
    >
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
</template>

<script setup>
/**
 * ResourcePicker.vue — generic Phase 2 picker for the Project Resource
 * architecture (see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md).
 *
 * Deliberately minimal: a dropdown + clear button, driven entirely by the
 * resource registry's adapter for `resourceType`. It never imports a
 * concrete store (e.g. useDatasetStore) — everything goes through
 * getAdapter(resourceType), so the same component works for any future
 * resource type without changes here. Richer per-type UI (previews,
 * uploads, thumbnails) is out of scope for this phase and belongs in a
 * type-specific wrapper component, not in this one.
 */
import { computed } from 'vue'
import { getAdapter } from '../../stores/resourceRegistry.js'

const props = defineProps({
  resourceType: { type: String, required: true },
  modelValue: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

const items = computed(() => getAdapter(props.resourceType)?.list() ?? [])

function labelFor(item) {
  return getAdapter(props.resourceType)?.getDisplayName(item.uuid) ?? item.name ?? item.uuid
}

function onChange(uuid) {
  emit('update:modelValue', uuid)
}
</script>
